import React from "react";
import { Container } from "../layout/Container";
import sim3dPreview from "../../assets/simulation-3d-preview.png";

// ── Mini visual mockups for each step ────────────────────────────────────────

function CargoMockup() {
  const rows = [
    { name: "Carton A",    l: "50", w: "40", h: "30", kg: "12",  qty: "48", color: "#2F8EF4" },
    { name: "Carton B",    l: "60", w: "50", h: "45", kg: "18",  qty: "24", color: "#06B6D4" },
    { name: "Heavy Box",   l: "80", w: "60", h: "60", kg: "34",  qty: "12", color: "#1B66DF" },
    { name: "Pallet Wrap", l: "40", w: "40", h: "20", kg: "5",   qty: "60", color: "#39A2FF" },
  ];
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/80 p-4 text-xs font-mono">
      <div className="mb-2 grid grid-cols-[1fr_24px_24px_24px_30px_24px] gap-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-500 px-0.5">
        {["Item", "L", "W", "H", "kg", "Qty"].map((h) => (
          <span key={h}>{h}</span>
        ))}
      </div>
      {rows.map((r) => (
        <div key={r.name} className="grid grid-cols-[1fr_24px_24px_24px_30px_24px] gap-1.5 items-center py-1.5 border-t border-white/5 px-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="h-2 w-2 rounded-sm shrink-0" style={{ backgroundColor: r.color }} />
            <span className="text-white truncate text-[10px]">{r.name}</span>
          </div>
          {[r.l, r.w, r.h, r.kg, r.qty].map((v, i) => (
            <span key={i} className="text-slate-400 text-[10px]">{v}</span>
          ))}
        </div>
      ))}
      <div className="mt-3 flex flex-wrap gap-2">
        <span className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] text-slate-400">hand-stuffed</span>
        <span className="rounded-md border border-primary/30 bg-primary/10 px-2 py-1 text-[10px] text-primary">152 packages</span>
        <span className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-[10px] text-cyan-400">3 stacking rules</span>
      </div>
    </div>
  );
}

function AlgorithmMockup() {
  const options = [
    { label: "1 × 20ft Standard", cost: "$1,800", verdict: "skip", pct: 143 },
    { label: "2 × 20ft Standard", cost: "$3,600", verdict: "skip", pct: 88  },
    { label: "1 × 40ft Standard", cost: "$2,600", verdict: "best", pct: 82  },
  ];
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/80 p-4 space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Evaluating combinations…</p>
      {options.map((o) => (
        <div key={o.label} className={`rounded-lg px-3 py-2 border ${o.verdict === 'best' ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-white/5 bg-white/[0.02]'}`}>
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold truncate ${o.verdict === 'best' ? 'text-white' : 'text-slate-500 line-through'}`}>{o.label}</p>
              <div className="mt-1 h-1 w-full rounded-full bg-white/10">
                <div
                  className={`h-full rounded-full ${o.pct > 100 ? 'bg-red-500' : o.verdict === 'best' ? 'bg-emerald-400' : 'bg-slate-600'}`}
                  style={{ width: `${Math.min(o.pct, 100)}%` }}
                />
              </div>
            </div>
            <span className={`text-xs font-black shrink-0 ${o.verdict === 'best' ? 'text-emerald-400' : 'text-slate-600'}`}>{o.cost}</span>
            {o.verdict === 'best' && (
              <span className="shrink-0 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-black text-white">BEST</span>
            )}
          </div>
          {o.pct > 100 && (
            <p className="mt-1 text-[10px] font-bold text-red-400">Over capacity ({o.pct}%)</p>
          )}
        </div>
      ))}
    </div>
  );
}

function ResultMockup() {
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/80 p-4 space-y-3">
      {/* Real 3D simulation screenshot */}
      <div className="relative rounded-lg overflow-hidden border border-white/10">
        <img
          src={sim3dPreview}
          alt="3D simulation result"
          width="276"
          height="140"
          className="w-full object-cover"
          style={{ maxHeight: "140px", objectPosition: "center" }}
          loading="lazy"
          decoding="async"
        />
        {/* Overlay play hint */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/80 backdrop-blur-sm">
            <span className="material-symbols-outlined text-[16px] text-white" style={{ fontVariationSettings: "'FILL' 1" }}>play_arrow</span>
          </div>
        </div>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Cost",   value: "$2,900" },
          { label: "Weight", value: "864 kg"  },
          { label: "Volume", value: "4.32 m³" },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-white/8 bg-white/[0.03] p-2 text-center">
            <p className="text-[10px] text-slate-500">{s.label}</p>
            <p className="text-xs font-black text-white">{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const steps = [
  {
    number: "01",
    title: "Define your cargo",
    description: "Enter box dimensions, weights, quantities, and stacking rules. Add pallets if needed. Under 2 minutes for a full shipment.",
    detail: "Supports hand-stuffed and palletized modes. Custom orientations and fragility rules.",
    visual: <CargoMockup />,
  },
  {
    number: "02",
    title: "Engine finds the cheapest container mix",
    description: "Our algorithm evaluates every combination (20ft, 40ft, 40ft HC) and finds the one that costs least while fitting everything.",
    detail: "Every combination is checked automatically, not just the obvious one — so the answer is actually the cheapest, not just a fit.",
    visual: <AlgorithmMockup />,
  },
  {
    number: "03",
    title: "Get a 3D plan + full cost breakdown",
    description: "See every carton in a live 3D simulation: play, pause, seek. Share the result link with your team. Cost and utilisation per container.",
    detail: "Shareable link. Exportable cost report. Container-level weight, volume, and item details.",
    visual: <ResultMockup />,
  },
];

export function HowItWorksSection() {
  return (
    <section id="how-it-works" className="bg-slate-900/50 py-24">
      <Container>
        <div className="mx-auto mb-16 flex max-w-2xl flex-col gap-4 text-center">
          <span className="text-sm font-bold uppercase tracking-widest text-primary bg-primary/10 px-4 py-1.5 rounded-full w-fit mx-auto border border-primary/20">
            How It Works
          </span>
          <h3 className="text-3xl font-black leading-tight md:text-4xl text-white">
            From cargo list to optimal plan{" "}
            <span className="bg-gradient-to-r from-primary via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              in under 2 seconds.
            </span>
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
          {steps.map((step, i) => (
            <div key={step.number} className="flex flex-col gap-5">
              {/* Visual mockup — fixed height so all 3 align */}
              <div className="relative h-[280px]">
                <div className="absolute inset-0 overflow-hidden rounded-xl">
                  {step.visual}
                </div>
                {/* Step badge */}
                <span className="absolute -top-3 -left-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-black text-white shadow-lg shadow-primary/40">
                  {i + 1}
                </span>
              </div>
              {/* Text */}
              <div className="flex flex-col gap-2">
                <h4 className="text-base font-black text-white">{step.title}</h4>
                <p className="text-sm leading-relaxed text-slate-300">{step.description}</p>
                <p className="text-xs text-slate-500">{step.detail}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div className="mt-16 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { value: "< 2s",   label: "Simulation time" },
            { value: "2-in-1", label: "Selection + packing" },
            { value: "16+",    label: "Container types" },
            { value: "100%",   label: "API-accessible" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-white/8 bg-white/[0.03] p-5 text-center">
              <p className="text-3xl font-black text-white">{s.value}</p>
              <p className="mt-1 text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}
